import {
  UsageTrackerEventTypes,
  UsageTrackerIdentifyEvent,
  UsageTrackerPageEvent,
  UsageTrackerTrackEvent,
} from '@csp/csp-common-analytics';
import { StorageService } from '@csp/web-storage';

const allEvents: UsageTrackerEventTypes[] = [] as const;
const STORAGE_KEY_EVENTS = `DebugAnalyticsTrackerService_events` as const;
const STORAGE_KEY_IS_ENABLED = `DebugAnalyticsTrackerService_isEnabled` as const;
const VERBOSE = true as const;

const loadEventsFromStorage = (): void => {
  const serializedEvents = StorageService.sessionStorage.getItem(STORAGE_KEY_EVENTS);
  if (!serializedEvents) {
    return;
  }
  const events = JSON.parse(serializedEvents) as UsageTrackerEventTypes[];

  if (!Array.isArray(events)) {
    return;
  }

  allEvents.push(...events);
};

type IsEnabledHandler = (isEnabled: boolean) => void;
const isEnabledListeners = new Map<number, IsEnabledHandler>();
let isEnabledCurrentId = 0;

const addIsEnabledListener = (handler: IsEnabledHandler): number => {
  isEnabledCurrentId = isEnabledCurrentId + 1;
  isEnabledListeners.set(isEnabledCurrentId, handler);
  handler(isEnabled);
  return isEnabledCurrentId;
};

const removeIsEnabledListener = (id: number): void => {
  isEnabledListeners.delete(id);
};

let isEnabled = StorageService.sessionStorage.getItem(STORAGE_KEY_IS_ENABLED) === 'true';
const setIsEnabled = (enabled: boolean): void => {
  isEnabled = enabled;
  StorageService.sessionStorage.setItem(STORAGE_KEY_IS_ENABLED, enabled);
  isEnabledListeners.forEach(handler => handler(enabled));
};

const page = async (event: UsageTrackerPageEvent): Promise<void> => {
  if (!isEnabled) {
    return;
  }

  allEvents.push(event);
  StorageService.sessionStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(allEvents, null, 2));

  if (VERBOSE) {
    console.log(`Analytics page event: ${JSON.stringify(event, null, 2)}`);
  }
};

const track = async (event: UsageTrackerTrackEvent): Promise<void> => {
  if (!isEnabled) {
    return;
  }

  allEvents.push(event);
  StorageService.sessionStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(allEvents, null, 2));

  if (VERBOSE) {
    console.log(`Analytics track event: ${JSON.stringify(event, null, 2)}`);
  }
};

const identify = async (event: UsageTrackerIdentifyEvent): Promise<void> => {
  if (!isEnabled) {
    return;
  }

  allEvents.push(event);
  StorageService.sessionStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(allEvents, null, 2));

  if (VERBOSE) {
    console.log(`Analytics identify event: ${JSON.stringify(event, null, 2)}`);
  }
};

const toJSON = (): string => JSON.stringify(allEvents, null, 2);

const clear = (): void => {
  allEvents.length = 0;
  StorageService.sessionStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(allEvents, null, 2));
};

// Initialize and load events from storage when loading the module
loadEventsFromStorage();

export const DebugAnalyticsTrackerService = {
  setIsEnabled,
  isEnabled: (): boolean => isEnabled,
  page,
  track,
  identify,
  toJSON,
  clear,
  addIsEnabledListener,
  removeIsEnabledListener,
};
